import React, { useState } from 'react';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import APopover from '../APopover';
import HoverIcon from '../HoverIcon/HoverIcon';
import { smartDateString } from '../../utils/date';
import Icon from '../Icon/Icon';
import {
  MentionList,
  MentionItem,
  MentionBody,
  MentionDate,
  MentionListBody,
  MentionListHeader,
} from './styles';
import Button from '../Button/Button';
import { TaskOptionsIcon } from '../TaskEditorHeader/styles';
import { blue, exactWhite, lightBlue, red } from '../../utils/style/colors';
import SanitizedHTML from 'react-sanitized-html';
import { navigate } from '@reach/router';
import Input from '../Input/Input';
import { PopoverPlacement } from '../PopoverBase/types';
import { appDispatch, RootState } from 'store/store';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import { IMention } from 'store/tasks/tasksType';
import { resolveMention, resolveMentionFn } from 'store/tasks/tasksSlice';
import i18n from '../../i18n';

interface IOwnProps {
  mentions: IMention[];
  small?: boolean;
  title?: string;
  companyId?: number;
  placement?: PopoverPlacement;
  projectId?: number;
  embedded?: boolean;
  conversationPanelToggle?: (shouldShow: boolean) => void;
  isHoverIcon?: boolean;
}
interface IStateToProps {
  thisUser: IThisUserState;
}
interface IDispatchToProps {
  resolveMention: (
    mentionId?: number,
    taskId?: number
  ) => ReturnType<typeof resolveMentionFn>;
}
type IComponentProps = IOwnProps & IStateToProps & IDispatchToProps;
let enableAutoDismiss = false;
const MentionPop: React.FC<IComponentProps> = ({
  resolveMention,
  mentions,
  small,
  thisUser,
  title,
  companyId,
  projectId,
  placement,
  embedded = false,
  conversationPanelToggle,
  isHoverIcon = false,
}) => {
  const mentionsCopy = [...mentions];
  const mentionCount = mentions.reduce(
    (current: any, item: IMention) => {
      if (item.directAddress) {
        current.directAddress++;
        if (item.event.type === 'CommentEvent') current.directComment++;
      } else {
        current.info++;
      }
      return current;
    },
    { directAddress: 0, info: 0, directComment: 0 }
  );
  const autoDismiss = !mentions.find(
    (m) => m.event.type === 'CommentEvent' && m.directAddress
  );
  const [renderCount, setRenderCount] = useState(0);
  const [loadings, setLoadings] = useState(new Set());
  const [showPop, setShowPop] = useState(false);
  const sorted = mentionsCopy.sort((a, b) => b.id - a.id);
  const hasDirectAddress = mentionCount.directAddress > 0;
  const directComment = mentionCount.directComment > 0;

  if (mentions.length === 0) {
    return <span />;
  }

  const onDismiss = () => {
    if (enableAutoDismiss && autoDismiss && mentions.length) {
      resolveMention(undefined, mentions[0].taskId);
    }
  };
  return (
    <APopover
      placement={placement}
      target={
        <span style={{ margin: 'auto 0' }}>
          {isHoverIcon ? (
            <HoverIcon
              type={
                directComment
                  ? 'message-circle'
                  : hasDirectAddress
                    ? 'at-sign'
                    : 'info'
              }
              color={directComment ? red : hasDirectAddress ? blue : lightBlue}
              size={small ? '15px' : undefined}
            />
          ) : (
            <Icon
              type={
                directComment
                  ? 'message-circle'
                  : hasDirectAddress
                    ? 'at-sign'
                    : 'info'
              }
              color={exactWhite}
              // color={directComment ? red : hasDirectAddress ? blue : lightBlue}
            />
          )}
        </span>
      }
      trigger="click"
      arrow
      curveBorder
      show={showPop}
      onShowChange={() => setShowPop((prev) => !prev)}
      // onShowChange={(show) => onShowChange(show)}
      onDismiss={onDismiss}
    >
      <MentionListBody
        onClick={(e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <MentionListHeader>
          {title && <div>{title}</div>}
          {autoDismiss && (
            <div
              onClick={() => {
                enableAutoDismiss = !enableAutoDismiss;
                setRenderCount(renderCount + 1);
              }}
            >
              <Input
                id={'autoDimissCheck'}
                type={'checkbox'}
                checked={enableAutoDismiss}
                label={i18n.t('autoDismiss', { ns: 'general' }) as string}
              />
            </div>
          )}
          {(!autoDismiss || !enableAutoDismiss) && mentions.length > 1 && (
            <Button
              style={{ padding: '2px 4px' }}
              color="red"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const taskId = mentions[0].taskId;
                loadings.add('t' + taskId);
                setLoadings(loadings);
                resolveMention(undefined, taskId).then(() => {
                  loadings.delete('t' + taskId);
                  setLoadings(loadings);
                  if (loadings.size === 0) onDismiss();
                  setShowPop(false);
                });
              }}
              loading={loadings.has('t' + mentions[0].taskId)}
            >
              {i18n.t('resolveAllMentions', { ns: 'general' }) as string}
              <Icon type="x" />
            </Button>
          )}
        </MentionListHeader>
        <MentionList>
          {sorted.map((mention: IMention) => {
            if (loadings.has(mention.id)) {
              return undefined;
            }
            const text = (
              mention.directAddress
                ? mention.event.objectText
                : mention.event.text
            )
              .replaceAll(
                RegExp(`@\\[U${thisUser?.userId}]\\(([^)]+)\\)`, 'g'),
                `<b style="color:${blue}">@$1</b>`
              )
              .replaceAll(
                /@\[U[0-9]+]\(([^)]+)\)/g,
                `<b style="color:${lightBlue}">@$1</b>`
              );
            return (
              <MentionItem key={mention.id}>
                <Icon
                  type={
                    mention.event.type === 'CommentEvent'
                      ? 'message-circle'
                      : mention.directAddress
                        ? 'at-sign'
                        : 'info'
                  }
                  color={
                    mention.event.type === 'CommentEvent'
                      ? red
                      : mention.directAddress
                        ? blue
                        : lightBlue
                  }
                />
                <MentionBody
                  onClick={() => {
                    if (embedded) {
                      if (mention.event.type === 'CommentEvent') {
                        conversationPanelToggle?.(true);
                      }
                    } else {
                      const isHome = window.location.href.includes('home');
                      let url = isHome
                        ? `/t/${companyId}/home`
                        : `/t/${companyId}/${
                            projectId ? `project/${projectId}/task` : 'task'
                          }`;

                      url += `/editor/${mention.taskId}/${mention.event.type === 'CommentEvent' ? 'conversation' : 'info'}`;
                      navigate(url);
                    }
                    setShowPop(false);
                  }}
                >
                  <SanitizedHTML
                    allowedAttributes={{ b: ['style'] }}
                    allowedTags={['b']}
                    html={text}
                  />
                </MentionBody>
                <MentionDate>
                  {smartDateString(mention.creationDate, true)}
                </MentionDate>
                <TaskOptionsIcon
                  type="x"
                  onClick={(e: MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    loadings.add('t' + mention.id);
                    setLoadings(loadings);
                    resolveMention(mention.id, mention.taskId);
                  }}
                />
              </MentionItem>
            );
          })}
        </MentionList>
      </MentionListBody>
    </APopover>
  );
};

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state
) => {
  const thisUser = state.thisUser;
  return { thisUser };
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => ({
  resolveMention: (mentionId?: number, taskId?: number) => {
    return appDispatch(resolveMention({ mentionId, taskId }));
  },
  appDispatch,
});
export default connect(mapStateToProps, mapDispatchToProps)(MentionPop);
